import { useMemo, useState } from 'react';

import type { DocumentResolversSettings } from '../../../components/Editor/types/document-resolvers-settings.type';
import type { FileStorage } from '../../../components/Editor/types/file-storage.type';
import type { ResolvableFieldSettings } from '../../../components/ResolvableFields/types/resolvable-field-settings.type';

import { MaterialsSelectionFormStep } from '../../MaterialUsage/steps/Selection';
import { MaterialsUsageFormStep } from '../../MaterialUsage/steps/Usage';
import { CostsSelectionFormStep } from '../../CostUsage/steps/Selection';
import { CostsUsageFormStep } from '../../CostUsage/steps/Usage';
import { MaterialsOrderFormStep } from '../../MaterialUsage/steps/Order';
import { CostsOrderFormStep } from '../../CostUsage/steps/Order';

import { ResolvableServiceFormTab } from '../enums/tab.enum';
import { ResolvableServiceInfoFormStep } from '../steps/Info';
import { ResolvableServiceQuotationInfoFormStep } from '../steps/QuotationInfo';

interface Params {
  readonly documentResolversSettings: DocumentResolversSettings;
  readonly formContext?: string;
  readonly isEnabled?: boolean;
  readonly resolvableFieldSettings: ResolvableFieldSettings;
  readonly stepIdsSubset?: readonly ResolvableServiceFormTab[];
  readonly storage: FileStorage;
}

export const useResolvableServiceSteps = ({
  documentResolversSettings,
  formContext,
  isEnabled = true,
  resolvableFieldSettings,
  stepIdsSubset,
  storage,
}: Readonly<Params>) => {
  const [isUsingMaterials, setIsUsingMaterials] = useState(false);
  const [isUsingCosts, setIsUsingCosts] = useState(false);

  const { allTabs, steps, wizardChildren } = useMemo(() => {
    const resolvableServiceInfo = {
      step: {
        id: ResolvableServiceFormTab.ResolvableServiceInfo,
        title: 'Informations Service',
        isEnabled,
      },
      child: (
        <ResolvableServiceInfoFormStep
          documentResolversSettings={documentResolversSettings}
          formContext={formContext}
          id={ResolvableServiceFormTab.ResolvableServiceInfo}
          key={ResolvableServiceFormTab.ResolvableServiceInfo}
          storage={storage}
        />
      ),
    };

    const resolvableServiceValue = {
      step: {
        id: ResolvableServiceFormTab.ResolvableServiceQuotationInfo,
        title: 'Informations Devis',
        isEnabled,
      },
      child: (
        <ResolvableServiceQuotationInfoFormStep
          formContext={formContext}
          id={ResolvableServiceFormTab.ResolvableServiceQuotationInfo}
          key={ResolvableServiceFormTab.ResolvableServiceQuotationInfo}
          resolvableFieldSettings={resolvableFieldSettings}
        />
      ),
    };

    const materialsSelection = {
      step: {
        id: ResolvableServiceFormTab.MaterialsSelection,
        title: 'Liaison aux Matériels',
        isEnabled,
      },
      child: (
        <MaterialsSelectionFormStep
          formContext={formContext}
          id={ResolvableServiceFormTab.MaterialsSelection}
          key={ResolvableServiceFormTab.MaterialsSelection}
          setIsUsingMaterials={setIsUsingMaterials}
        />
      ),
    };

    const materialsOrder = {
      step: {
        id: ResolvableServiceFormTab.MaterialsOrder,
        title: 'Ordre des Matériels',
        isEnabled: isEnabled && isUsingMaterials,
      },
      child: (
        <MaterialsOrderFormStep
          formContext={formContext}
          id={ResolvableServiceFormTab.MaterialsOrder}
          key={ResolvableServiceFormTab.MaterialsOrder}
        />
      ),
    };

    const materialsUsage = {
      step: {
        id: ResolvableServiceFormTab.MaterialsUsage,
        title: 'Paramétrage des Matériels',
        isEnabled: isEnabled && isUsingMaterials,
      },
      child: (
        <MaterialsUsageFormStep
          formContext={formContext}
          id={ResolvableServiceFormTab.MaterialsUsage}
          key={ResolvableServiceFormTab.MaterialsUsage}
        />
      ),
    };

    const costsSelection = {
      step: {
        id: ResolvableServiceFormTab.CostsSelection,
        title: 'Liaison aux Coûts',
        isEnabled,
      },
      child: (
        <CostsSelectionFormStep
          formContext={formContext}
          id={ResolvableServiceFormTab.CostsSelection}
          key={ResolvableServiceFormTab.CostsSelection}
          setIsUsingCosts={setIsUsingCosts}
        />
      ),
    };

    const costsOrder = {
      step: {
        id: ResolvableServiceFormTab.CostsOrder,
        title: 'Ordre des Coûts',
        isEnabled: isEnabled && isUsingCosts,
      },
      child: (
        <CostsOrderFormStep
          formContext={formContext}
          id={ResolvableServiceFormTab.CostsOrder}
          key={ResolvableServiceFormTab.CostsOrder}
        />
      ),
    };

    const costsUsage = {
      step: {
        id: ResolvableServiceFormTab.CostsUsage,
        title: 'Paramétrage des Coûts',
        isEnabled: isEnabled && isUsingCosts,
      },
      child: (
        <CostsUsageFormStep
          formContext={formContext}
          id={ResolvableServiceFormTab.CostsUsage}
          key={ResolvableServiceFormTab.CostsUsage}
        />
      ),
    };

    const allTabs = [
      [ResolvableServiceFormTab.ResolvableServiceInfo, resolvableServiceInfo],
      [
        ResolvableServiceFormTab.ResolvableServiceQuotationInfo,
        resolvableServiceValue,
      ],
      [ResolvableServiceFormTab.MaterialsSelection, materialsSelection],
      [ResolvableServiceFormTab.MaterialsOrder, materialsOrder],
      [ResolvableServiceFormTab.MaterialsUsage, materialsUsage],
      [ResolvableServiceFormTab.CostsSelection, costsSelection],
      [ResolvableServiceFormTab.CostsOrder, costsOrder],
      [ResolvableServiceFormTab.CostsUsage, costsUsage],
    ] as const;

    const activeTabs = stepIdsSubset?.length
      ? allTabs.filter(([tab]) => stepIdsSubset.includes(tab))
      : allTabs;

    const steps = activeTabs.map(([, { step }]) => step);
    const wizardChildren = activeTabs.map(([, { child }]) => child);

    return { allTabs, steps, wizardChildren };
  }, [
    documentResolversSettings,
    formContext,
    isEnabled,
    isUsingCosts,
    isUsingMaterials,
    resolvableFieldSettings,
    stepIdsSubset,
    storage,
  ]);

  return { allTabs, steps, wizardChildren };
};
